import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Home } from "lucide-react";

import Networks from "./networks";
import Configs from "./configs";
import Operators from "./operators";
import News from "./news";
import WorkModes from "./work_modes";
import Sockets from "./sockets";
import Feedbacks from "./feedbacks";
import Admins from "./admins";
import Reports from "./reports";
import ServicesAdmins from "./services";
import SensorsAdmins from "./sensors";
import LicensePage from "./license";
import AdminPolls from "./polls";
import AdminProfile from "./profile";
import AdminWidgets from "./widgets";
import { fetchUnreadAdminFeedbacks } from "../services/feedbacks";
import { fetchUnreadAdminPolls, markAdminPollsViewed } from "../services/polls";
import { useWebSocket } from "../context/WebSocketContext"
import { getMe } from "../services/session";
import { pushAndroidBackHandler } from "../utils/androidBack";
import { consumePendingAdminPushOpen, registerStoredPushToken } from "../utils/pushInit";
import ThemeSelector from "../components/ThemeSelector";

const AdminPage = () => {
    const [selectedSection, setSelectedSection] = useState(null);
    const [adminName, setAdminName] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [openGroupId, setOpenGroupId] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [hasUnreadFeedbacks, setHasUnreadFeedbacks] = useState(false);
    const [hasNews, setHasNews] = useState(false);
    const [hasPolls, setHasPolls] = useState(false);
    const [wsEnabled, setWsEnabled] = useState(false);
    const [serverOffsetMs, setServerOffsetMs] = useState(null);
    const [serverTick, setServerTick] = useState(0);
    const [showTime, setShowTime] = useState(() => window.innerWidth >= 520);
    const navigate = useNavigate();

    const navbarRef = useRef(null);
    const drawerRef = useRef(null);

    const { connect, subscribe, unsubscribe } = useWebSocket();
    useEffect(() => {
        if (!wsEnabled) return undefined;

        connect("feedbacks", `admin`);
        connect("news", `admin`);
        connect("polls", `admin`);

        const onNewFeedback = () => {
            setHasUnreadFeedbacks(true);
        };

        const onNewsReceived = () => {
            setHasNews(true);
        };

        const onPollReceived = () => {
            setHasPolls(true);
        };

        const onPollVote = () => {
            window.dispatchEvent(new Event("wr_polls_changed"));
        };

        subscribe(
            "feedbacks",
            `admin`,
            "feedback_new",
            onNewFeedback
        );

        subscribe(
            "news",
            `admin`,
            "news_new",
            onNewsReceived
        );

        subscribe(
            "polls",
            `admin`,
            "poll_new",
            onPollReceived
        );

        subscribe(
            "polls",
            `admin`,
            "poll_vote",
            onPollVote
        );

        return () => {
            unsubscribe("feedbacks", `admin`, "feedback_new", onNewFeedback);
            unsubscribe("news", `admin`, "news_new", onNewsReceived);
            unsubscribe("polls", `admin`, "poll_new", onPollReceived);
            unsubscribe("polls", `admin`, "poll_vote", onPollVote);
        };

    }, [connect, subscribe, unsubscribe, wsEnabled]);

    useEffect(() => {
        const loadMe = async () => {
            const me = await getMe();
            if (!me) {
                setWsEnabled(false);
                return navigate("/");
            }

            setAdminName(me.name || "");
            setIsSuperAdmin(me.is_superadmin === true);
            setWsEnabled(true);

            const serverMs = Date.parse(me.server_time_utc);
            if (!Number.isNaN(serverMs)) {
                setServerOffsetMs(serverMs - Date.now());
            }
        };

        loadMe();
    }, [navigate]);

    useEffect(() => {
        const onResize = () => setShowTime(window.innerWidth >= 520);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                setMenuOpen(false);
                setOpenGroupId(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!openGroupId) return;
        const onDocMouseDown = (e) => {
            const target = e.target;
            if (navbarRef.current && navbarRef.current.contains(target)) return;
            if (drawerRef.current && drawerRef.current.contains(target)) return;
            setOpenGroupId(null);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [openGroupId]);

    useEffect(() => {
        if (serverOffsetMs === null) return;
        const id = setInterval(() => setServerTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, [serverOffsetMs]);

    const formatKyiv = (d, withTime) => {
        if (!d) return "";
        const opts = {
            timeZone: "Europe/Kyiv",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
        };
        return new Intl.DateTimeFormat("uk-UA", opts).format(d).replace(",", "");
    };

    const serverNow = serverOffsetMs === null ? null : new Date(Date.now() + serverOffsetMs);
    // eslint-disable-next-line no-unused-vars
    const _serverTick = serverTick;
    const serverDateText = serverNow ? formatKyiv(serverNow, false) : "";
    const serverDateTimeText = serverNow ? formatKyiv(serverNow, true) : "";

    useEffect(() => {
        const loadUnreadFeedbacks = async () => {
            try {
                const data = await fetchUnreadAdminFeedbacks();
                setHasUnreadFeedbacks(data.has_unread === true);
            } catch (err) {
                console.error("Помилка при отриманні фідбеків:", err);
            }
        };
        loadUnreadFeedbacks();
    }, []);

    useEffect(() => {
        const loadUnreadPolls = async () => {
            try {
                const data = await fetchUnreadAdminPolls();
                setHasPolls(data.has_unread === true);
            } catch (err) {
                console.error("Помилка при отриманні опитувань:", err);
            }
        };
        loadUnreadPolls();
    }, []);

    const menuItems = {
        profile: { name: "Мій профіль", key: "profile", icon: "🪪" },
        admins: { name: "Адміністратори", key: "admins", icon: "👤" },
        operators: { name: "Оператори", key: "operators", icon: "👥" },
        networks: { name: "Мережі", key: "networks", icon: "🌐" },
        sockets: { name: "Модуль СмартДоступ", key: "sockets", icon: "🔌" },
        workModes: { name: "Режими роботи", key: "work_modes", icon: "⏱️" },
        news: { name: "Новини", key: "news", icon: "📰" },
        polls: { name: "Опитування", key: "polls", icon: "🗳️" },
        feedbacks: { name: "Відгуки", key: "feedbacks", icon: "📝" },
        license: { name: "Ліцензія", key: "license", icon: "🔐" },
        services: { name: "Сервіси", key: "services", icon: "🛠️" },
        sensors: { name: "Сенсори", key: "sensors", icon: "🚨" },
        configs: { name: "Конфігурація", key: "configs", icon: "⚙️" },
        reports: { name: "Аналітика", key: "reports", icon: "📊" },
    };

    const menuGroups = [
        {
            id: "account",
            title: "Акаунт",
            items: [menuItems.profile],
        },
        {
            id: "access",
            title: "Доступи",
            items: [menuItems.admins, menuItems.operators],
        },
        {
            id: "infra",
            title: "Інфраструктура",
            items: [menuItems.networks, menuItems.sockets, menuItems.workModes],
        },
        {
            id: "comm",
            title: "Комунікація",
            items: [menuItems.news, menuItems.polls, menuItems.feedbacks],
        },
        {
            id: "system",
            title: "Система",
            items: [
                menuItems.license,
                menuItems.services,
                menuItems.sensors,
                ...(isSuperAdmin ? [menuItems.configs] : []),
            ],
        },
        {
            id: "analytics",
            title: "Аналітика",
            items: [menuItems.reports],
        },
    ];

    const pageTitles = {
        profile: "Мій профіль",
        admins: "Адміністратори",
        operators: "Оператори",
        networks: "Мережі",
        sockets: "Модуль СмартДоступ",
        work_modes: "Режими / Ціни",
        news: "Новини",
        polls: "Опитування",
        feedbacks: "Відгуки клієнтів",
        license: "Ліцензія",
        services: "Сервіси",
        sensors: "Сенсори пристроїв",
        configs: "Системні значення",
        reports: "Аналітика",
    };

    const currentPageTitle = selectedSection ? pageTitles[selectedSection] || null : "Головна";

    const handleSectionClick = async (key) => {
        setMenuOpen(false);
        setOpenGroupId(null);
        setSelectedSection(key);
        if (key === "feedbacks") setHasUnreadFeedbacks(false);
        if (key === "news") setHasNews(false);
        if (key === "polls") {
            setHasPolls(false);
            try {
                await markAdminPollsViewed();
            } catch (_) {
                // ignore
            }
        }
    };

    const goHome = useCallback(() => {
        setMenuOpen(false);
        setOpenGroupId(null);
        setSelectedSection(null);
    }, []);

    useEffect(() => {
        registerStoredPushToken().catch(() => {});
    }, []);

    useEffect(() => {
        const openFromPush = () => {
            const pending = consumePendingAdminPushOpen();
            if (!pending?.kind) return;
            if (pending.kind === "license") {
                setSelectedSection("license");
                setMenuOpen(false);
                setOpenGroupId(null);
                return;
            }
            if (pending.kind === "news") {
                setSelectedSection("news");
                return;
            }
            if (pending.kind === "poll") {
                setSelectedSection("polls");
                return;
            }
            if (pending.kind === "feedback") {
                setSelectedSection("feedbacks");
            }
        };
        openFromPush();
        window.addEventListener("wrPushOpen", openFromPush);
        return () => window.removeEventListener("wrPushOpen", openFromPush);
    }, []);

    useEffect(() => {
        return pushAndroidBackHandler(() => {
            if (menuOpen) {
                setMenuOpen(false);
                return true;
            }
            if (openGroupId) {
                setOpenGroupId(null);
                return true;
            }
            if (selectedSection) {
                setSelectedSection(null);
                return true;
            }
            return false;
        });
    }, [menuOpen, openGroupId, selectedSection]);

    return (
        <>
            {/* Header */}
            <div className="header-bar">
                <div className="header-left">
                    <div className="header-greeting">
                        <div className="header-greeting-top">
                            {selectedSection ? (
                                <button
                                    type="button"
                                    className="header-home-btn"
                                    onClick={goHome}
                                    onPointerUp={(e) => e.currentTarget.blur()}
                                    aria-label="На головну"
                                    title="На головну"
                                >
                                    <Home size={16} strokeWidth={2.4} aria-hidden="true" />
                                </button>
                            ) : null}
                            <div className="header-greeting-line">
                                {adminName ? (
                                    <>Вітаю, <span>{adminName}</span>!</>
                                ) : (
                                    <>Вітаю, адміністраторе!</>
                                )}
                            </div>
                        </div>

                        {(currentPageTitle || serverNow) && (
                            <div className="header-meta-row">
                                {currentPageTitle ? (
                                    <div className="header-page-title" title={currentPageTitle}>
                                        {currentPageTitle}
                                    </div>
                                ) : <div className="header-page-title" />}

                                {serverNow && (
                                    <div className="header-datetime">
                                        {showTime ? serverDateTimeText : serverDateText}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="navbar-container" ref={navbarRef}>
                    {menuGroups.map((group) => {
                        const isOpen = openGroupId === group.id;
                        const hasGroupDot =
                            group.id === "comm" && (hasUnreadFeedbacks || hasNews || hasPolls);

                        return (
                            <div className="nav-group" key={group.id}>
                                <button
                                    type="button"
                                    className={`nav-group-button ${isOpen ? "open" : ""}`}
                                    onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                    aria-expanded={isOpen}
                                >
                                    <span className="nav-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                    <span className="nav-group-text">{group.title}</span>
                                    {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                </button>

                                {isOpen && (
                                    <div className="nav-group-panel" role="menu">
                                        {group.items.map(({ key, name, icon }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => handleSectionClick(key)}
                                                className={`nav-panel-button ${selectedSection === key ? "active" : ""}`}
                                                role="menuitem"
                                            >
                                                <span className="nav-icon">{icon}</span>
                                                <span className="nav-text">{name}</span>
                                                {key === "feedbacks" && hasUnreadFeedbacks && <span className="nav-dot" />}
                                                {key === "news" && hasNews && <span className="nav-dot" />}
                                                {key === "polls" && hasPolls && <span className="nav-dot" />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <button
                    className={`header-hamburger ${menuOpen ? "is-open" : ""}`}
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-label="Меню"
                >
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                </button>
            </div>

            <div className={`sidebar-drawer ${menuOpen ? "open" : ""}`} ref={drawerRef}>
                <div className="drawer-head">
                    <div className="drawer-head__copy">
                        <strong className="drawer-head__title">Меню</strong>
                        <span className="drawer-head__subtitle">Розділи адміністратора</span>
                    </div>
                    <div className="drawer-head__theme">
                        <ThemeSelector compact />
                    </div>
                </div>
                <div className="drawer-scroll">
                    <div className="drawer-menu">
                        {menuGroups.map((group) => {
                            const isOpen = openGroupId === group.id;
                            const hasGroupDot =
                                group.id === "comm" && (hasUnreadFeedbacks || hasNews || hasPolls);

                            return (
                                <div className="drawer-group" key={group.id}>
                                    <button
                                        type="button"
                                        className={`drawer-group-button ${isOpen ? "open" : ""}`}
                                        onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="drawer-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                        <span className="drawer-group-text">{group.title}</span>
                                        {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                    </button>

                                    {isOpen && (
                                        <div className="drawer-group-items">
                                            {group.items.map(({ key, name, icon }) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => handleSectionClick(key)}
                                                    className={`drawer-button ${selectedSection === key ? "active" : ""}`}
                                                >
                                                    <span className="nav-icon">{icon}</span>
                                                    <span className="nav-text">{name}</span>
                                                    {key === "feedbacks" && hasUnreadFeedbacks && <span className="nav-dot" />}
                                                    {key === "news" && hasNews && <span className="nav-dot" />}
                                                    {key === "polls" && hasPolls && <span className="nav-dot" />}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {menuOpen && <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} />}

            <div className={`shell-bottom-dock ${menuOpen ? "is-menu-open" : ""}`} aria-label="Навігація">
                <button
                    type="button"
                    className="shell-bottom-dock__btn"
                    onClick={goHome}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label="На головну"
                    title="На головну"
                >
                    <Home size={18} strokeWidth={2.4} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className={`shell-bottom-dock__btn shell-bottom-dock__btn--menu ${menuOpen ? "is-open" : ""}`}
                    onClick={() => setMenuOpen((open) => !open)}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label={menuOpen ? "Закрити меню" : "Меню"}
                    aria-expanded={menuOpen}
                    title={menuOpen ? "Закрити меню" : "Меню"}
                >
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                </button>
            </div>

            <div
                className="main-content"
                onClick={() => {
                    if (menuOpen) setMenuOpen(false);
                }}
            >
                {!selectedSection ? (
                    <AdminWidgets
                        onNavigate={handleSectionClick}
                        hasNews={hasNews}
                        hasPolls={hasPolls}
                        hasUnreadFeedbacks={hasUnreadFeedbacks}
                    />
                ) : null}
                {selectedSection === "profile" && <AdminProfile />}
                {selectedSection === "admins" && <Admins />}
                {selectedSection === "networks" && <Networks isSuperAdmin={isSuperAdmin} />}
                {selectedSection === "configs" && isSuperAdmin && <Configs />}
                {selectedSection === "operators" && <Operators />}
                {selectedSection === "license" && <LicensePage isSuperAdmin={isSuperAdmin} />}
                {selectedSection === "sockets" && <Sockets />}
                {selectedSection === "work_modes" && <WorkModes />}
                {selectedSection === "services" && <ServicesAdmins />}
                {selectedSection === "sensors" && <SensorsAdmins isSuperAdmin={isSuperAdmin} />}
                {selectedSection === "news" && <News />}
                {selectedSection === "polls" && <AdminPolls onPollsViewed={() => setHasPolls(false)} />}
                {selectedSection === "feedbacks" && <Feedbacks />}
                {selectedSection === "reports" && <Reports />}
            </div>
        </>
    );
};

export default AdminPage;
